/**
 * The copilot's tool path — registry-driven, not hand-wired.
 *
 * Every agent verb a module registers in `core.mcp_tool` is reachable here, by
 * compiling the same spec with the same interpreter the MCP connector uses
 * (`specToTool`, @solum/sdk). Registering a verb therefore lights it up on BOTH
 * surfaces at once: an external agent over MCP, and the admin typing into the
 * console. Nothing here grants anything — every verb still gates itself
 * server-side and RLS is the floor, so the copilot refuses exactly what the
 * console refuses.
 *
 * The alternative — an `if (/grant a role/.test(q))` per verb — is how the demo
 * brain started, and it does not scale past the handful someone remembered to
 * write. It also silently omits the verbs you need most in a hurry: before this
 * existed the copilot could `assign_role` but had no way to reach `revoke_role`.
 */
import {
  specToTool, invocationRecorder, runRecorded,
  type SolumClient, type SolumTool, type McpToolSpec, type InvocationRecorder,
} from '@solum/sdk'

export interface ToolRun {
  tool: string
  mutates: boolean
  result: unknown
}

let cache: { at: number; tools: SolumTool[] } | null = null
const TTL_MS = 60_000

/** Every verb the caller's licence and identity expose, compiled and runnable. */
export async function loadTools(client: SolumClient): Promise<SolumTool[]> {
  if (cache && Date.now() - cache.at < TTL_MS) return cache.tools
  // AI surfaces consume the governed manifest, never the raw registry. Ordinary
  // UI discovery still uses describeTools(): search and relationship lookup are
  // not model execution and must not silently inherit AI budgets or policy.
  const manifest = await client.resolveAiToolManifest([], 200, ['rpc', 'entity', 'builtin'])
    .catch(() => null)
  const specs = manifest?.tools as McpToolSpec[] | null | undefined
  const tools = Array.isArray(specs) ? specs.map((s) => specToTool(s)) : []
  cache = { at: Date.now(), tools }
  return tools
}

/** Drop the memoised registry — call after a change that adds or removes verbs. */
export function invalidateTools() { cache = null }

/** Property names a tool accepts, read off its declared JSON Schema. */
export function toolParams(tool: SolumTool): string[] {
  const schema = tool.inputSchema as { properties?: Record<string, unknown> }
  return Object.keys(schema.properties ?? {})
}

/**
 * Parse an explicit invocation. Two forms, both unambiguous on purpose — a
 * copilot that GUESSES which permission verb you meant is not one you should
 * hand a revoke to:
 *
 *   /revoke_role {"user_id":"…","role_key":"finance"}
 *   revoke_role user_id=… role_key=finance
 */
export interface ParsedInvocation {
  name: string
  args: Record<string, unknown>
  /**
   * The user wrote the `/` sigil, i.e. they NAMED a verb rather than typed a
   * sentence whose first word happens to parse as one. Load-bearing for
   * recording — see `runTool`.
   */
  explicit: boolean
}

export function parseInvocation(query: string): ParsedInvocation | null {
  const trimmed = query.trim()
  const m = /^\s*(\/?)([a-z][a-z0-9_]*)\s*([\s\S]*)$/i.exec(trimmed)
  if (!m?.[2]) return null
  const explicit = m[1] === '/'
  const name = m[2]
  const rest = (m[3] ?? '').trim()
  if (!rest) return { name, args: {}, explicit }

  if (rest.startsWith('{')) {
    try { return { name, args: JSON.parse(rest) as Record<string, unknown>, explicit } } catch { return null }
  }

  // key=value pairs; quoted values may contain spaces
  const args: Record<string, unknown> = {}
  const pair = /([a-z_][a-z0-9_]*)=("[^"]*"|'[^']*'|\S+)/gi
  let hit: RegExpExecArray | null
  while ((hit = pair.exec(rest))) {
    const raw = hit[2]!
    const val = /^["']/.test(raw) ? raw.slice(1, -1) : raw
    args[hit[1]!] = val === 'true' ? true : val === 'false' ? false : val
  }
  return Object.keys(args).length ? { name, args, explicit } : null
}

/**
 * The #396 recording seam on the CONSOLE surface (@solum/sdk, shared with the
 * MCP connector and the copilot relay).
 *
 * `core.tool_invocation` was measured EMPTY on a live box, because only those
 * two surfaces ever wrote to it — the console's own copilot, which is where an
 * admin actually runs verbs, recorded nothing at all. The seam already existed
 * and was simply not called from here.
 *
 * Written with the caller's own token through `core.record_tool_invocation`, so
 * the row names the granted principal (server-derived, never asserted — rule
 * 11), and best-effort: a failed record must never break the verb it records.
 * Structured signal only — the tool name, the outcome, the duration, a truncated
 * reason. NEVER the query, the args, or the result.
 */
const recorderFor = new WeakMap<object, InvocationRecorder>()
function recorder(client: SolumClient): InvocationRecorder {
  const existing = recorderFor.get(client as unknown as object)
  if (existing) return existing
  // The record carries the call's correlation id as a HEADER (#2444).
  const made = invocationRecorder((fn, args, correlationId) =>
    (correlationId ? client.withCorrelation(correlationId) : client).raw().rpcScalar(fn, args, 'core'))
  recorderFor.set(client as unknown as object, made)
  return made
}

/**
 * Run a named verb, recording the invocation. Returns null when no such verb is
 * registered.
 *
 * ── Why an unknown verb is recorded only when EXPLICIT ──────────────────────
 * The MCP connector and the relay both record an unknown tool name as a
 * refusal, and they are right to: on those surfaces the name came from an agent
 * deliberately naming a tool. The console is different. `parseInvocation` parses
 * a LONE WORD as an invocation of that name, so `demoBrain` asks this about
 * `mrr` (and anything else typed as one word) on the way to its natural-language
 * branches. Recording those would write a refused invocation for a question that
 * was answered perfectly well, burying the real refusals in the log and putting
 * nonsense in the user's own history panel — telemetry that misinforms is worse
 * than none (rule 10's argument, one surface over).
 *
 * The `/verb` sigil is the line: it means the user named a verb, so a verb that
 * does not exist is a genuine governed refusal and is recorded as one. A bare
 * word that matches nothing was never an invocation at all, and the brain
 * carries on to its next branch.
 */
export async function runTool(
  client: SolumClient, name: string, args: Record<string, unknown>,
  opts: { explicit?: boolean } = {},
): Promise<ToolRun | null> {
  const tools = await loadTools(client)
  const tool = tools.find((t) => t.name === name)
  if (!tool) {
    if (opts.explicit) {
      await recorder(client)({
        tool: name, surface: 'copilot', outcome: 'refused', durationMs: 0,
        reason: `unknown tool '${name}' (unregistered, unentitled, or disabled by tool policy)`,
      })
    }
    return null
  }
  return runRecorded(recorder(client), tool.name, 'copilot', async (correlationId) => {
    // The relay's refusal, verbatim (#2933): proposal-only until a governed
    // human act confirms. Both the `/verb` and bare paths of demoBrain land here.
    if (tool.requiresConfirmation) {
      throw new Error(`tool '${tool.name}' requires governed human confirmation`)
    }
    // The verb's own requests carry the same id as its record (#2444).
    const result = await tool.handler(client.withCorrelation(correlationId), args)
    if (tool.mutates) invalidateTools()
    return { tool: tool.name, mutates: tool.mutates, result }
  })
}

/** The verbs on offer, grouped for a help reply. Security verbs listed first. */
export async function describeAvailable(client: SolumClient, filter?: string): Promise<string> {
  const tools = await loadTools(client)
  const want = filter?.toLowerCase()
  const shown = want ? tools.filter((t) => t.name.includes(want) || t.description.toLowerCase().includes(want)) : tools
  if (!shown.length) return `No registered verb matches “${filter}”.`
  const reads = shown.filter((t) => !t.mutates).map((t) => t.name)
  const writes = shown.filter((t) => t.mutates).map((t) => t.name)
  return [
    reads.length ? `Read: ${reads.join(', ')}` : '',
    writes.length ? `Change: ${writes.join(', ')}` : '',
    `Run one with \`/<name> key=value\` — every verb is checked server-side, so I can only do what you could.`,
  ].filter(Boolean).join('\n')
}
